import { type ReactElement, useEffect, useState } from 'react'

import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import Avatar from '@mui/material/Avatar'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'

import { validateImage } from 'global/helpers/validators'
import type { KNFormDropzoneItemProps } from './types'

const KNFormDropzoneItem = ({
  file,
  pending,
  disabled,
  onDelete,
  children,
}: KNFormDropzoneItemProps): ReactElement => {
  const [previewSrc, setPreviewSrc] = useState('')

  useEffect(() => {
    setPreviewSrc('')
    if (!pending && validateImage(file.type)) {
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setPreviewSrc(reader.result as string)
      }, false)
      reader.readAsDataURL(file)
    }
  }, [pending])

  return (
    <ListItem
      disableGutters
      disablePadding
      secondaryAction={
        onDelete && (
          <IconButton
            onClick={(event): void => {
              event.stopPropagation()
              onDelete()
            }}
            disabled={disabled}
            sx={{
              color: 'primary.light',
            }}
          >
            <ClearOutlinedIcon fontSize="small" />
          </IconButton>
        )
      }
    >
      <ListItemButton
        sx={{
          padding: 0,
        }}
        onClick={(): void => {
          window.open(URL.createObjectURL(file))
        }}
      >
        <ListItemAvatar>
          <Avatar
            variant="square"
            sx={{
              color: 'primary.light',
              background: 'transparent',
              width: '3rem',
              height: '3rem',
            }}
            src={previewSrc}
          >
            {pending ? (
              <CircularProgress size={24} />
            ) : (
              <InsertDriveFileOutlinedIcon />
            )}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <Typography
              component="p"
              variant="p3"
              noWrap
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {file.name}
            </Typography>
          }
          secondary={children}
          sx={{
            margin: 0,
          }}
        />
      </ListItemButton>
    </ListItem>
  )
}

export default KNFormDropzoneItem
